import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiContentSaveOutline } from "@mdi/js";
import { Box } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCreateBillOfMaterialsMgmtRequest } from "../../../../__generated__/orval/endpoints/bill-of-materials-mgmt-requests/bill-of-materials-mgmt-requests";
import { NewBillOfMaterialsMgmtRequestForm } from "./-internal-components/NewBillOfMaterialsMgmtRequestForm";
import {
  buildCreateBillOfMaterialsMgmtRequestPayload,
  type NewBillOfMaterialsMgmtRequestFormSchema,
  newBillOfMaterialsMgmtRequestFormDefaultValues,
  newBillOfMaterialsMgmtRequestFormSchema,
} from "./-schemas/newBillOfMaterialsMgmtRequestSchema";

const BillOfMaterialsMgmtRequest: React.FC = () => {
  const { t } = useTranslation();

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: newBillOfMaterialsMgmtRequestFormDefaultValues,
    resolver: zodResolver(newBillOfMaterialsMgmtRequestFormSchema),
  });

  const { mutate, isPending } = useCreateBillOfMaterialsMgmtRequest({
    mutation: {
      onError: () => {
        enqueueSnackbar(t("mmRequestChild.errorMessage"), {
          variant: "error",
        });
      },
      onSuccess: (response) => {
        enqueueSnackbar(t("mmRequestChild.successMessage"), {
          variant: "success",
        });
        window.location.href = `/vbuyer/mm_requests?flash_message=${t("mmRequestChild.flashMessage")}${response.mm_request?.id}`;
      },
    },
  });

  const handleFilesChange = useCallback(
    (fileAttachments: string[]) => {
      setValue("file_attachments", fileAttachments);
    },
    [setValue],
  );

  const onSubmit: SubmitHandler<NewBillOfMaterialsMgmtRequestFormSchema> =
    useCallback(
      (formValues) => {
        const payload =
          buildCreateBillOfMaterialsMgmtRequestPayload(formValues);

        mutate({
          data: payload,
        });
      },
      [mutate],
    );

  return (
    <PageContainer
      backTo="/vbuyer/mm_requests/create_new_requests"
      maxWidth="md"
      showBackButton
      title={t("common.billOfMaterialsMgmtRequest")}
    >
      <AppCard>
        <NewBillOfMaterialsMgmtRequestForm
          control={control}
          onFilesChange={handleFilesChange}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              color="success"
              loading={isPending}
              startIcon={<Icon icon={mdiContentSaveOutline} />}
              type="submit"
            >
              {t("common.save")}
            </Button>
          </Box>
        </NewBillOfMaterialsMgmtRequestForm>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/bill_of_materials_mgmt_requests/new",
)({
  component: BillOfMaterialsMgmtRequest,
});
